import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import AppHeader from '../../../components/AppHeader.tsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarCheck,
  faChartBar,
  faChartLine,
  faEuroSign,
  faSpinner,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { bookingsApi } from '@api/api';
import { BookingDtoStateEnum, BookingSummaryDto } from '@api/generated';

const formatAmt = (n: number) =>
  new Intl.NumberFormat('cs-CZ', { style: 'currency', currency: 'CZK', maximumFractionDigits: 0 }).format(n);

const PerformancePage: React.FC = () => {
  const { entityId } = useParams<{ entityId: string }>();
  const [summary, setSummary] = useState<BookingSummaryDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const breadcrumb = [{ label: 'Dashboard' }, { label: 'Statistiky výkonu' }];

  useEffect(() => {
    if (!entityId) return;
    const dateFrom = dayjs().subtract(30, 'day').format('YYYY-MM-DD');
    const dateTo = dayjs().format('YYYY-MM-DD');
    setLoading(true);
    bookingsApi
      .getBookingSummary({ salonId: entityId, dateFrom, dateTo })
      .then(res => setSummary(res.data))
      .catch(err => setError(err?.message ?? 'Chyba načítání'))
      .finally(() => setLoading(false));
  }, [entityId]);

  const totalBookings = summary?.totalBookings ?? 0;
  const totalRevenue = summary?.totalRevenue ?? 0;
  const completed = (summary?.bookingsByState ?? {})[BookingDtoStateEnum.Completed] ?? 0;
  const avgPrice = totalBookings > 0 ? totalRevenue / totalBookings : 0;

  const performanceStats = [
    { title: 'Celkové rezervace', value: totalBookings.toLocaleString('cs-CZ'), icon: faCalendarCheck, color: 'bg-blue-50', iconColor: 'text-blue-600' },
    { title: 'Dokončené služby', value: completed.toLocaleString('cs-CZ'), icon: faChartBar, color: 'bg-green-50', iconColor: 'text-green-600' },
    { title: 'Celkové tržby', value: formatAmt(totalRevenue), icon: faEuroSign, color: 'bg-purple-50', iconColor: 'text-purple-600' },
    { title: 'Průměrná cena', value: formatAmt(avgPrice), icon: faChartLine, color: 'bg-orange-50', iconColor: 'text-orange-600' },
  ];

  const staffPerformance = useMemo(() => {
    return [...(summary?.topWorkers ?? [])].sort((a, b) => b.revenue - a.revenue);
  }, [summary]);

  const serviceStats = useMemo(() => {
    const list = [...(summary?.revenueByService ?? [])].sort((a, b) => b.revenue - a.revenue);
    const total = list.reduce((acc, s) => acc + s.revenue, 0) || 1;
    return list.map(s => ({ ...s, percentage: (s.revenue / total) * 100 }));
  }, [summary]);

  return (
    <div className="h-full flex flex-col">
      <AppHeader title="Statistiky výkonu" breadcrumb={breadcrumb} />

      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="p-6 space-y-6">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">{error}</div>}

          <section className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {performanceStats.map((stat, index) => (
              <div key={index} className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center">
                  <div className={`p-3 rounded-lg ${stat.color}`}>
                    <FontAwesomeIcon icon={stat.icon} className={`text-xl ${stat.iconColor}`} />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                    <div className="flex items-center">
                      {loading && <FontAwesomeIcon icon={faSpinner} className="animate-spin text-gray-400 mr-2" />}
                      <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </section>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow-sm">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Výkon zaměstnanců</h3>
                <p className="text-xs text-gray-500 mt-1">Posledních 30 dní</p>
              </div>
              <div className="p-6">
                {staffPerformance.length === 0 ? (
                  <p className="text-sm text-gray-500">Žádná data.</p>
                ) : (
                  <div className="space-y-4">
                    {staffPerformance.map(staff => (
                      <div key={staff.workerId} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                        <div className="flex items-center space-x-4">
                          <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                            <FontAwesomeIcon icon={faUsers} className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{staff.workerName}</p>
                            <p className="text-sm text-gray-600">{staff.bookingCount} rezervací</p>
                          </div>
                        </div>
                        <p className="font-medium text-green-600">{formatAmt(staff.revenue)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Statistiky služeb</h3>
                <p className="text-xs text-gray-500 mt-1">Posledních 30 dní</p>
              </div>
              <div className="p-6">
                {serviceStats.length === 0 ? (
                  <p className="text-sm text-gray-500">Žádná data.</p>
                ) : (
                  <div className="space-y-4">
                    {serviceStats.map(service => (
                      <div key={service.serviceId} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-900">{service.serviceName}</span>
                          <span className="text-sm text-gray-600">
                            {service.bookingCount} ({service.percentage.toFixed(0)}%)
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-green-600 h-2 rounded-full transition-all duration-300" style={{ width: `${service.percentage}%` }} />
                        </div>
                        <div className="flex justify-between text-xs text-gray-500">
                          <span>{formatAmt(service.revenue)}</span>
                          <span>{service.percentage.toFixed(1)}% z celku</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformancePage;
